import { Banner, Button, Checkbox, Dialog, Tooltip, Typography } from '@neo4j-ndl/react';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import { consoleApi, getApiError, getErrorMessage } from '@nx/state';
import type { IpFilter } from '@nx/state';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SyntheticEvent } from 'react';
import { useState } from 'react';

const logger = createLogger(APP_SCOPE.aura);

type Props = {
  onClose: () => void;
  ipFilter: IpFilter;
};

export const DeleteIpFilterDialog = ({ onClose, ipFilter }: Props) => {
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [deleteIpFilter, deleteIpFilterRes] = consoleApi.useDeleteIpFilterMutation();

  const handleConfirm = (checked: boolean) => {
    setHasConfirmed(checked);
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    deleteIpFilter({ id: ipFilter.id })
      .unwrap()
      .then(() => {
        onClose();
      })
      .catch((error: FetchBaseQueryError | SerializedError | undefined) => {
        const apiError = getApiError(error);
        const message = getErrorMessage(apiError);
        logger.error(message);
      });
  };

  const filteredEntities = [
    ...(ipFilter.filteredEntities?.instances ?? []),
    ...(ipFilter.filteredEntities?.projects ?? []),
    ...(ipFilter.filteredEntities?.organizations ?? []),
  ];

  return (
    <Dialog isOpen onClose={onClose}>
      <Dialog.Header>Delete IP Filter: {ipFilter.name}</Dialog.Header>
      <form onSubmit={handleSubmit}>
        <Dialog.Content className="flex flex-col gap-5">
          <div className="my-4">
            <Typography variant="body-medium" className="text-neutral-text-weak">
              Deleting an IP Filter will remove it from all associated instances, projects, and organizations, blocking
              all traffic to the previously filtered instances.
            </Typography>
          </div>
          {filteredEntities.length > 0 ? (
            <Banner
              hasIcon
              type="warning"
              description="Deleting this filter when it is in use may cause disruptions requiring manual resolution. Customer Support will be unable to assist with resulting issues."
              usage="inline"
            />
          ) : (
            <Banner
              hasIcon
              type="info"
              description="This IP Filter is not in use, so deleting it should not cause any disruptions."
              usage="inline"
            />
          )}
          {deleteIpFilterRes.isError && (
            <Banner
              hasIcon
              type="danger"
              description={getErrorMessage(getApiError(deleteIpFilterRes.error))}
              usage="inline"
            />
          )}
        </Dialog.Content>
        <Dialog.Actions className="flex items-center justify-between">
          <Checkbox
            isChecked={hasConfirmed}
            onChange={(event) => handleConfirm(event.target.checked)}
            label="I understand"
          />
          <div className="flex gap-4 ">
            <Button color="neutral" fill="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Tooltip type="simple" isDisabled={hasConfirmed} placement="top" isPortaled={false}>
              <Tooltip.Trigger hasButtonWrapper>
                <Button color="danger" type="submit" isLoading={deleteIpFilterRes.isLoading} isDisabled={!hasConfirmed}>
                  Delete
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content className="!fixed !w-[200px]">
                You need to confirm that you understand the consequences of deleting an IP filter
              </Tooltip.Content>
            </Tooltip>
          </div>
        </Dialog.Actions>
      </form>
    </Dialog>
  );
};
